import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  defaultValue,
  employeeCoursesReportFilterSchema,
} from 'Pages/reports/[submodules]/employees/schemas/employee-courses-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import type { EmployeeCoursesReportFilterI } from 'types';
import usePrioritiesApi from './usePrioritiesApi';
import useCourseCategoriesApi from './useCourseCategoriesApi';
import useDebounce from './useDebounce';

interface Props {
  onSearch: (filters: EmployeeCoursesReportFilterI) => void;
}

const useEmployeeCoursesReportFilters = ({ onSearch }: Props) => {
  const {
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(employeeCoursesReportFilterSchema),
    defaultValues: defaultValue,
  });

  const { data: prioritiesData, loading: prioritiesLoading, error: prioritiesError } = usePrioritiesApi({ init: true });

  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCourseCategoriesApi({ init: true });

  const handleReset = () => {
    reset(defaultValue);
    onSearch({});
  };

  // Watch for changes in the name field
  const nameValue = watch('name');
  const debouncedName = useDebounce(nameValue, 500);

  // Effect to handle debounced search
  useEffect(() => {
    if (debouncedName !== undefined) {
      const values = getValues();
      onSearch(values);
    }
  }, [debouncedName]);

  // Handle other filter changes immediately
  const handleFilterChange = () => {
    const values = getValues();
    onSearch(values);
  };

  return {
    control,
    reset,
    setValue,
    getValues,
    isDirty,
    handleReset,
    handleFilterChange,
    // Priorities
    prioritiesData,
    prioritiesLoading,
    prioritiesError,
    // Course categories
    categoriesData,
    categoriesLoading,
    categoriesError,
  };
};

export default useEmployeeCoursesReportFilters;
